// Encryption of the offline copy (OFFLINE_ENCRYPTION). Pure WebCrypto, used by
// the page, the service worker and the search worker; no imports, so it also
// runs under `node --test`.
//
// Keys: the server hands out a per-user data key (DK, 32 raw bytes) while
// online. Two non-extractable subkeys are derived from it with HKDF: `enc`
// (AES-GCM, file blocks + records) and `name` (HMAC, opaque cache keys). For
// offline use the raw DK is stored only wrapped with a key derived from the
// user's password (PBKDF2), see wrapDataKey/unwrapDataKey.
//
// File format (Cache Storage body): "ODE1" + 8-byte random IV base, then the
// plaintext in BLOCK-byte blocks, each AES-GCM encrypted (ciphertext + 16-byte
// tag). Block IV = base || u32 index; AAD = cacheKey|index|final, so blocks
// cannot be reordered, swapped between files or cut off at the end. Block
// boundaries are computable, so a Range request decrypts only its blocks.

const subtle = globalThis.crypto.subtle;
const enc = new TextEncoder();
const dec = new TextDecoder();

export const BLOCK = 64 * 1024;
const TAG = 16;
const CBLOCK = BLOCK + TAG;
const MAGIC = [0x4f, 0x44, 0x45, 0x31]; // "ODE1"
export const HEADER = 12;
export const PBKDF2_ITER = 600000;

// ---------------------------------------------------------------- encoding
export function toB64(bytes) {
  const u = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let s = "";
  for (let i = 0; i < u.length; i += 0x8000) s += String.fromCharCode(...u.subarray(i, i + 0x8000));
  return btoa(s);
}
export function fromB64(s) {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
const hex = (buf) => [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");

// ---------------------------------------------------------------- keys
export async function importDataKey(raw) {
  const base = await subtle.importKey("raw", raw, "HKDF", false, ["deriveKey"]);
  const derive = (info, alg, usages) => subtle.deriveKey(
    { name: "HKDF", hash: "SHA-256", salt: new Uint8Array(0), info: enc.encode(info) },
    base, alg, false, usages);
  return {
    enc: await derive("od-enc-v1", { name: "AES-GCM", length: 256 }, ["encrypt", "decrypt"]),
    name: await derive("od-name-v1", { name: "HMAC", hash: "SHA-256", length: 256 }, ["sign"]),
  };
}

// Opaque cache key for a document path; the origin keeps it a valid URL.
export async function opaqueKey(keys, path, origin) {
  const mac = await subtle.sign("HMAC", keys.name, enc.encode(path));
  return `${origin}/od/f/${hex(mac).slice(0, 40)}`;
}

// ---------------------------------------------------------------- blocks
function blockIv(base, index) {
  const iv = new Uint8Array(12);
  iv.set(base, 0);
  new DataView(iv.buffer).setUint32(8, index);
  return iv;
}
const blockAad = (aadKey, index, final) => enc.encode(`${aadKey}|${index}|${final ? 1 : 0}`);

async function encBlock(key, base, aadKey, index, plain, final) {
  return new Uint8Array(await subtle.encrypt(
    { name: "AES-GCM", iv: blockIv(base, index), additionalData: blockAad(aadKey, index, final) }, key, plain));
}
async function decBlock(key, base, aadKey, index, cipher, final) {
  return new Uint8Array(await subtle.decrypt(
    { name: "AES-GCM", iv: blockIv(base, index), additionalData: blockAad(aadKey, index, final) }, key, cipher));
}

// Plaintext size and block count from the stored ciphertext size.
export function plainLayout(cipherSize) {
  const body = cipherSize - HEADER;
  if (body < TAG) throw new Error("od: truncated file");
  const blocks = Math.ceil(body / CBLOCK);
  return { blocks, size: body - blocks * TAG };
}

// Streaming encryption: plaintext ReadableStream → "ODE1" + blocks. One block
// is held back so the last one can carry final=1 (an empty file is one empty
// final block).
export function encryptStream(keys, aadKey, source) {
  const base = globalThis.crypto.getRandomValues(new Uint8Array(8));
  const reader = source.getReader();
  let buf = new Uint8Array(0);
  let pending = null;
  let index = 0;
  let started = false;
  let done = false;
  return new ReadableStream({
    async pull(ctrl) {
      if (!started) { started = true; ctrl.enqueue(new Uint8Array([...MAGIC, ...base])); return; }
      for (;;) {
        if (buf.length >= BLOCK || (done && buf.length)) {
          const block = buf.subarray(0, BLOCK);
          buf = buf.slice(block.length);
          if (pending) { ctrl.enqueue(await encBlock(keys.enc, base, aadKey, index++, pending, false)); pending = block.slice(); return; }
          pending = block.slice();
          continue;
        }
        if (done) {
          ctrl.enqueue(await encBlock(keys.enc, base, aadKey, index++, pending || new Uint8Array(0), true));
          ctrl.close();
          return;
        }
        const { value, done: d } = await reader.read();
        if (d) { done = true; continue; }
        const merged = new Uint8Array(buf.length + value.length);
        merged.set(buf, 0); merged.set(value, buf.length);
        buf = merged;
      }
    },
    cancel(reason) { return reader.cancel(reason); },
  });
}

async function readSlice(blob, start, end) {
  return new Uint8Array(await blob.slice(start, end).arrayBuffer());
}
async function readBase(blob) {
  const head = await readSlice(blob, 0, HEADER);
  if (head.length < HEADER || MAGIC.some((m, i) => head[i] !== m)) throw new Error("od: not an encrypted file");
  return head.subarray(4, 12);
}

// Decrypt plaintext bytes [start, end) of an encrypted blob.
export async function decryptRange(keys, aadKey, blob, start, end) {
  const base = await readBase(blob);
  const { blocks, size } = plainLayout(blob.size);
  end = Math.min(end, size);
  if (start >= end) return new Uint8Array(0);
  const first = Math.floor(start / BLOCK);
  const last = Math.floor((end - 1) / BLOCK);
  const out = new Uint8Array(end - start);
  let pos = 0;
  for (let i = first; i <= last; i++) {
    const off = HEADER + i * CBLOCK;
    const plain = await decBlock(keys.enc, base, aadKey, i, await readSlice(blob, off, off + CBLOCK), i === blocks - 1);
    const from = i === first ? start - i * BLOCK : 0;
    const to = i === last ? end - i * BLOCK : plain.length;
    out.set(plain.subarray(from, to), pos);
    pos += to - from;
  }
  return out;
}

// Whole file as a decrypting stream (one block in memory at a time).
export function decryptStream(keys, aadKey, blob) {
  let base = null;
  let i = 0;
  let blocks = 0;
  return new ReadableStream({
    async pull(ctrl) {
      if (!base) { base = await readBase(blob); blocks = plainLayout(blob.size).blocks; }
      if (i >= blocks) { ctrl.close(); return; }
      const off = HEADER + i * CBLOCK;
      ctrl.enqueue(await decBlock(keys.enc, base, aadKey, i, await readSlice(blob, off, off + CBLOCK), i === blocks - 1));
      i++;
    },
  });
}

// "bytes=a-b" | "bytes=a-" | "bytes=-n" → [start, endExclusive] or null (unsatisfiable).
export function parseRange(header, size) {
  const m = /^bytes=(\d*)-(\d*)$/.exec((header || "").trim());
  if (!m || (m[1] === "" && m[2] === "")) return null;
  let start;
  let end;
  if (m[1] === "") { const n = Number(m[2]); start = Math.max(0, size - n); end = size; }
  else { start = Number(m[1]); end = m[2] === "" ? size : Math.min(Number(m[2]) + 1, size); }
  if (start >= size || start >= end) return null;
  return [start, end];
}

// ---------------------------------------------------------------- records
// One encrypted blob per record: iv(12) || AES-GCM(JSON). `label` (table|id)
// is the AAD, so a record cannot be moved to another row.
export async function encryptJson(keys, label, value) {
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
  const ct = new Uint8Array(await subtle.encrypt(
    { name: "AES-GCM", iv, additionalData: enc.encode(label) }, keys.enc, enc.encode(JSON.stringify(value))));
  const out = new Uint8Array(12 + ct.length);
  out.set(iv, 0); out.set(ct, 12);
  return out;
}
export async function decryptJson(keys, label, blob) {
  const u = blob instanceof Uint8Array ? blob : new Uint8Array(blob);
  const plain = await subtle.decrypt(
    { name: "AES-GCM", iv: u.subarray(0, 12), additionalData: enc.encode(label) }, keys.enc, u.subarray(12));
  return JSON.parse(dec.decode(plain));
}

// ---------------------------------------------------------------- password wrap
async function passwordKey(password, salt, iter) {
  const base = await subtle.importKey("raw", enc.encode(password), "PBKDF2", false, ["deriveKey"]);
  return subtle.deriveKey({ name: "PBKDF2", hash: "SHA-256", salt, iterations: iter },
    base, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
}

export async function wrapDataKey(raw, password, meta = {}, iter = PBKDF2_ITER) {
  const salt = globalThis.crypto.getRandomValues(new Uint8Array(16));
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
  const kek = await passwordKey(password, salt, iter);
  const ct = await subtle.encrypt({ name: "AES-GCM", iv, additionalData: enc.encode("od-wrap-v1") }, kek, raw);
  return { v: 1, iter, salt: toB64(salt), iv: toB64(iv), ct: toB64(ct), ...meta };
}

// Raw DK bytes, or throws (wrong password / damaged wrap).
export async function unwrapDataKey(wrap, password) {
  const kek = await passwordKey(password, fromB64(wrap.salt), wrap.iter);
  return new Uint8Array(await subtle.decrypt(
    { name: "AES-GCM", iv: fromB64(wrap.iv), additionalData: enc.encode("od-wrap-v1") }, kek, fromB64(wrap.ct)));
}
